/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Icon } from '@components/atoms'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, MenuType } from '@redux/index'

/**
 * /admin/home — 키커 한 줄 + 관리자 메뉴 인덱스 행(번호 · 아이콘 · 이름 | 설명). 텍스트 로고는 쓰지 않는다(§13.3).
 * 배너·로티·카드 3열은 헤어라인 인덱스(홈 Support 문법)로 바꿨다. 설명 문구는 그대로다.
 */
const AdminHomePage = () => {
	// redux state
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)

	const ContentText = ({ menu }: { menu: MenuType }) => {
		return (
			<View className="rowDesc">
				{menu.id === 'adminAccessControl' ? (
					<>
						<p>주식회사 넥센의 모든 사원정보를 확인할 수 있습니다.</p>
						<p>사원들의 소중한 정보를 지켜주세요.</p>
						<p>우리는 단 하나의 정보도 외부로 흘러가지 않게</p>
						<p>안전한 정보보호를 이루는 정보전략팀입니다.</p>
					</>
				) : menu.id === 'adminMenu' ? (
					<>
						<p>부서의 정보를 확인하고 싶다면</p>
						<p>이 곳에서 모든 부서정보를 확인할 수 있습니다.</p>
						<p>단, 생성, 수정, 삭제는 그룹웨어서 가능합니다.</p>
					</>
				) : menu.id === 'adminIssueManagement' ? (
					<>
						<p>IAM이란?</p>
						<p>각 계정의 접근권한 관리를 의미합니다.</p>
						<p>특정 시스템에 사원의 접근 권한을 관리하고 싶다면</p>
						<p>이 곳에서 관리할 수 있습니다.</p>
						<p>그러나 권한부여는 신중하게 해주시기 바랍니다.</p>
					</>
				) : null}
			</View>
		)
	}

	return (
		<View css={style}>
			<View className="boWrap">
				<View className="boHeader">
					<span className="boKicker">Admin</span>
					<p className="boLead">개인정보는 안전하게, 권한부여는 신중하게.</p>
				</View>

				<View className="index">
					{adminMenu.map((aMenu, idx) => {
						return (
							<View key={aMenu.id} className="indexRow">
								<View className="rowLeft">
									<span className="boMeta rowNo">{String(idx + 1).padStart(2, '0')}</span>
									<Icon className="rowIcon" name={aMenu.icon ?? 'circle-medium'} />
									<Text as="h2" className="rowTitle" i18n={aMenu.i18n}>
										{aMenu.name}
									</Text>
								</View>
								<ContentText menu={aMenu} />
							</View>
						)
					})}
				</View>
			</View>
		</View>
	)
}

export default AdminHomePage

const style = css({
	...boShared,
	height: '100%',

	'.index': {
		'.indexRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
			gap: 32,
			padding: '28px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.rowLeft': { display: 'flex', alignItems: 'center', gap: 14 },
			'.rowNo': { width: 28, flexShrink: 0 },
			'.rowIcon': { fontSize: 20, color: mono.dim, flexShrink: 0 },
			'.rowTitle': {
				fontFamily: 'inherit',
				fontSize: 20,
				fontWeight: 300,
				lineHeight: 1.2,
				color: mono.ink,
				margin: 0,
			},
			'.rowDesc': {
				p: {
					fontSize: 14,
					fontWeight: 300,
					lineHeight: 1.7,
					color: mono.dim,
					maxWidth: 560,
				},
			},

			'@media (max-width: 900px)': { gridTemplateColumns: '1fr', gap: 12 },
		},
	},
})
